import styled from 'styled-components';
import getConfig from 'next/config';
import { useBreakpoint } from 'components/BreakpointProvider';
import { mediaMSmMax } from 'helpers/breakpoints';
import useAuthProvidersInitializer from 'components/auth/hooks/useAuthProvidersInitializer';
import {
  useAppleExchangeMutation,
  useTokenFacebookExchangeMutation,
  useTokenGoogleExchangeMutation,
} from 'components/auth/hooks/authFlowHooks';
import useIncompleteUserProcessing from 'components/auth/hooks/useIncompleteUserProcessing';
import { FlexCol } from 'components/primitives/flex';
import { LoginType } from 'auth/constants';
import { useTrackEvent } from 'hooks/useAnalyticsTrack';
import Cookies from 'js-cookie';
import { useLocalStorage } from 'beautiful-react-hooks';

const { GOOGLE_CLIENT_ID } = getConfig().publicRuntimeConfig;

const SocialLoginButtons = ({
  orText,
  compact = false,
  ignoreSmallScreen = false,
  isSignUpMode = false,
}: {
  orText: string;
  compact?: boolean;
  ignoreSmallScreen?: boolean;
  isSignUpMode?: boolean; // !isSignUpMode === isSignInMode
}) => {
  const eventIntakeTrack = useTrackEvent();
  const [galacticaSession] = useLocalStorage('galactica_session', '');

  const googleLogin = useTokenGoogleExchangeMutation();
  const facebookLogin = useTokenFacebookExchangeMutation();
  const appleExchangeMutation = useAppleExchangeMutation();
  const { handlePossiblyIncompleteAccountInAuth } = useIncompleteUserProcessing();

  const { appleInitialized } = useAuthProvidersInitializer();

  const logInWithGoogle = () => {
    if (isSignUpMode) {
      eventIntakeTrack('UserSignedUpWithGoogle', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    } else {
      eventIntakeTrack('UserSignedInWithGoogle', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    }

    window.gapi.load('auth2', async () => {
      const res = await window.gapi.auth2.init({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'profile email',
      });

      let user;

      if (res.isSignedIn.get()) {
        user = res.currentUser.get();
      } else {
        user = await window.gapi.auth2.getAuthInstance().signIn();
      }
      const authResponse = user.getAuthResponse();

      const googleResponse = await googleLogin.mutateAsync({
        token: authResponse.access_token,
      });
      await handlePossiblyIncompleteAccountInAuth(LoginType.GOOGLE_LOGIN, googleResponse.data.is_new_user);
    });
  };

  const logInWithFacebook = () => {
    if (isSignUpMode) {
      eventIntakeTrack('UserSignedUpWithFacebook', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    } else {
      eventIntakeTrack('UserSignedInWithFacebook', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    }

    // Facebook SDK does not accept async/await, so we need to use a callback
    const fbAsyncCallback = async (response) => {
      if (response.status === 'connected') {
        const { authResponse } = response;
        const facebookResponse = await facebookLogin.mutateAsync({
          token: authResponse.accessToken,
        });
        await handlePossiblyIncompleteAccountInAuth(LoginType.FACEBOOK_LOGIN, facebookResponse.data.is_new_user);
      }
    };

    window.FB.login(
      (response) => {
        fbAsyncCallback(response);
      },
      { scope: 'email' }
    );
  };

  const logInWithApple = async () => {
    if (isSignUpMode) {
      eventIntakeTrack('UserSignedUpWithApple', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    } else {
      eventIntakeTrack('UserSignedInWithApple', {
        anonymousId: Cookies.get('uuid') || '',
        sessionId: galacticaSession || '',
      });
    }

    const response = await window.AppleID.auth.signIn();
    const appleResponse = await appleExchangeMutation.mutateAsync(response);
    await handlePossiblyIncompleteAccountInAuth(LoginType.APPLE_LOGIN, appleResponse.data.user.is_new_user);
  };

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  return (
    <ButtonsWrapper>
      <Or>
        <hr />
        <span>{orText}</span>
        <hr />
      </Or>
      <ButtonWrapper gap="20px" className={(ignoreSmallScreen ? false : smallScreen || compact) && 'compact'}>
        <IconButton type="button" data-testid="facebook-auth-button" onClick={logInWithFacebook}>
          <Wrapper>
            <img height={17} alt="Facebook" src="/images/icons/facebook.svg" />
            <span>{(ignoreSmallScreen ? false : smallScreen || compact) ? 'Facebook' : 'Continue with Facebook'}</span>
          </Wrapper>
        </IconButton>
        <IconButton type="button" data-testid="google-auth-button" onClick={logInWithGoogle}>
          <Wrapper>
            <img height={17} alt="Google" src="/images/icons/google.svg" />
            <span>{(ignoreSmallScreen ? false : smallScreen || compact) ? 'Google' : 'Continue with Google'}</span>
          </Wrapper>
        </IconButton>
        <IconButton disabled={!appleInitialized} type="button" data-testid="apple-auth-button" onClick={logInWithApple}>
          <Wrapper>
            <img height={17} alt="Apple" src="/images/icons/apple.svg" />
            <span>{(ignoreSmallScreen ? false : smallScreen || compact) ? 'Apple' : 'Continue with Apple'}</span>
          </Wrapper>
        </IconButton>
      </ButtonWrapper>
    </ButtonsWrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  margin: auto;

  span,
  img {
    margin: auto 0;
  }

  span {
    margin-left: 10px;
    ${mediaMSmMax} {
      padding-top: 3px;
    }
  }
`;

const IconButton = styled.button`
  width: 100%;
  border: 1px solid #8e9697;
  border-radius: 6px;
  height: 55px;

  // ${mediaMSmMax} {
  //   height: 70px;
  // }

  &:disabled {
    opacity: 0.9;
  }

  font-weight: 500;
  font-size: 16px;
  display: flex;
`;

const Or = styled.div`
  display: flex;
  color: #8e9697;
  opacity: 0.5;

  font-weight: 500;
  font-size: 14px;

  padding: 20px 0;

  span {
    font-size: 12px;
    width: auto;
    white-space: pre;
    text-align: center;
    padding: 0 10px;
  }

  hr {
    margin-top: 8px;
    width: 100%;
    height: 0px;
  }
`;

const ButtonWrapper = styled(FlexCol)`
  &.compact {
    display: flex;
    flex-flow: row;
    gap: 20px;

    ${Wrapper} {
      font-size: 14px;
      flex-flow: column;

      span {
        margin: auto;
      }
    }
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: column;
`;

export default SocialLoginButtons;
